import { C8AttributeId, C8Node, C8NodeType } from "csi/typings";
import { ComponentItem } from "designSystem/types/component-item-types";
import React from "react";

export const defaultDateFormat = "M/d/yyyy";
export const defaultTimeFormat = "h:mm a";

export interface ILocaleComponentResources {
	RES_NEW_OF_TYPE: string;
	RES_UNNAMED: string;
}

export interface ILocaleProvider {
	/** resources used in standard components */
	resources: ILocaleComponentResources;

	/** return localized action name for given type */
	actionResource(nodeType: C8NodeType, actionId?: string): string;

	/** return localized attribute name for given type */
	attributeResource(nodeType: C8NodeType, id?: C8AttributeId): string;

	/** return attribute type localization */
	getAttributeType(type: string): string;

	/** returns localized name for given enum value */
	enumResource(enumValue: string): string;

	/** extracts the name from a component item */
	getComponentItemName(item: ComponentItem): string;

	/** extracts the slim name from a component item (combining name with name2) */
	getComponentItemSlimName(item: ComponentItem): string;

	/** return the date pattern based on user configs */
	getDatePattern(): string;

	/** return the message to display for the given error */
	getErrorMessage(error: Error): string;

	/** return a localized name, or "(Unnamed)" */
	getNameOrUnnamed(name: string): string;

	/** return the time pattern based on user configs */
	getTimePattern(): string;

	/** return the name of the node */
	getNodeName(node: C8Node): string;

	/** return localized text combining a number with the appropriate plural or singular form of the given type */
	numberOfType(n: number, nodeType: C8NodeType): string;

	/** return localized path name for given type */
	pathResource(nodeType: C8NodeType, path?: string): string;

	/** return localized text for given resource and parameters, which can include full dot notation */
	resource(res: string, resParams?: Array<number | string>): string;

	/** return localized name for given type in singular or plural form, with option of converting to lowercase */
	type(nodeType: C8NodeType, plural?: boolean, lower?: boolean): string;

	/** return localized view name for given type */
	viewResource(nodeType: C8NodeType, viewId?: string): string;
}

export function actionResource(nodeType: C8NodeType, actionId?: string): string {
	return actionId;
}

export function attributeResource(nodeType: C8NodeType, id?: C8AttributeId): string {
	return id;
}

export function enumResource(enumValue: string, uppercase = false): string {
	return uppercase ? enumValue.toUpperCase() : enumValue;
}

export function getAttributeType(type: string): string {
	return type;
}

export function getComponentItemName(item: ComponentItem): string {
	return item.name ?? item.id ?? "";
}

export function getComponentItemSlimName(item: ComponentItem): string {
	const name = getComponentItemName(item);
	const { name2 } = item;
	return name2 ? `${name} | ${name2}` : name;
}

export function getDatePattern(): string {
	return defaultDateFormat;
}

export function getErrorMessage(error: Error): string {
	return error.message;
}

export function getNameOrUnnamed(name: string): string {
	return name || resources.RES_UNNAMED;
}

export function getTimePattern(): string {
	return defaultTimeFormat;
}

export function getNodeName(node: C8Node): string {
	return node.$Name;
}

export function numberOfType(n: number, nodeType: C8NodeType): string {
	return `${n} ${type(nodeType, n !== 1)}`;
}

export function pathResource(nodeType: C8NodeType, path?: string): string {
	return path;
}

export function resource(res: string, resParams?: Array<number | string>): string {
	return resParams ? [res, ...resParams].join(",") : res;
}

export function type(nodeType: C8NodeType, plural = false, lower = false): string {
	const name = `${nodeType}${plural ? "s" : ""}`;
	return lower ? name.toLocaleLowerCase() : name;
}

export function viewResource(nodeType: C8NodeType, viewId?: string): string {
	return viewId;
}

export const resources: ILocaleComponentResources = {
	RES_NEW_OF_TYPE: "New ",
	RES_UNNAMED: "(No Name)",
};

// create context with default locale provider
const localeProvider: ILocaleProvider = {
	resources,
	actionResource,
	attributeResource,
	enumResource,
	getAttributeType,
	getComponentItemName,
	getComponentItemSlimName,
	getDatePattern,
	getErrorMessage,
	getNameOrUnnamed,
	getNodeName,
	getTimePattern,
	numberOfType,
	pathResource,
	resource,
	type,
	viewResource,
};

export const LocaleContext = React.createContext(localeProvider);

/**
 * Hook to use locale provider
 */
export function useLocale(): ILocaleProvider {
	return React.useContext(LocaleContext);
}
